/**
 * QueryState — one consistent way to handle a TanStack Query's
 * loading / error / empty / ready states.
 *
 * Before this component ~30 components hand-rolled their own
 * `isLoading ? ... : isError ? ... : empty ? ... : <chart>` triad with
 * slightly different markup, wording, and opacity. `QueryState` collapses
 * that to a single wrapper that reuses the shared `ChartLoading` /
 * `ChartEmpty` visuals so nothing looks new:
 *
 *   - `query.isLoading`  → <ChartLoading label={loadingLabel} />
 *   - `query.isError`    → <ChartEmpty message={errorLabel} />  (muted text)
 *   - `isEmpty(data)`    → <ChartEmpty message={emptyLabel} />
 *   - otherwise          → children(data)  — content rendered untouched
 *
 * The loading / error / empty views all fill their parent (`h-full w-full`,
 * centered) exactly like `ChartLoading` / `ChartEmpty` already do, so drop
 * `<QueryState>` into the same height-bound box the chart used to live in.
 *
 * Two escape hatches keep the API usable in every call site:
 *   - `errorLabel` accepts a `(error) => ReactNode` callback so call sites
 *     that surface the live `error.message` ("Error loading X: …") keep
 *     their dynamic text.
 *   - `minHeight` gives the loading / error / empty view a floor so it
 *     stays visible inside bare, un-sized `<div>`s where `h-full` would
 *     otherwise collapse to zero. Callers that already render inside a
 *     height-bound box (e.g. `<ChartCard height={…}>`) can omit it.
 *
 * Each call site keeps its own wording — pass it via `loadingLabel`,
 * `emptyLabel`, `errorLabel`. There is intentionally no app-wide default
 * string beyond `ChartEmpty`'s own fallback.
 */
import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import ChartLoading from './ChartLoading';
import ChartEmpty from './ChartEmpty';

/** Error label: a static string, or a callback fed the live query error. */
export type QueryStateErrorLabel = string | ((error: unknown) => ReactNode);

export interface QueryStateProps<T> {
  /**
   * The `useQuery` result whose `isLoading` / `isError` / `data` drive the
   * view. `error` is read only when `errorLabel` is a callback.
   */
  query: Pick<UseQueryResult<T>, 'isLoading' | 'isError' | 'data'> & {
    error?: unknown;
  };
  /** Predicate marking a successful-but-empty result. Defaults to `data == null`. */
  isEmpty?: (data: T) => boolean;
  /** Spinner label while loading. */
  loadingLabel?: string;
  /** Muted message shown when `isEmpty` is true. */
  emptyLabel?: string;
  /**
   * Muted message shown when the query errored. A string for static text,
   * or a `(error) => ReactNode` callback to render the live error message.
   */
  errorLabel?: QueryStateErrorLabel;
  /**
   * Minimum height for the loading / error / empty view. Use this when the
   * caller renders `<QueryState>` in a bare `<div>` with no height of its
   * own — `ChartLoading` / `ChartEmpty` are `h-full`, which collapses to
   * zero without a floor. Omit it inside an already height-bound box.
   */
  minHeight?: number | string;
  /** Optional className forwarded to the loading / error / empty view. */
  className?: string;
  /** Render the chart/table/content once data is present. */
  children: (data: T) => ReactNode;
}

/**
 * Renders the right view for a query's current state. When data is present
 * `children(data)` is rendered untouched — the chart/table itself is never
 * altered, only the transient loading/error/empty visuals are unified.
 */
export function QueryState<T>({
  query,
  isEmpty = (data) => data == null,
  loadingLabel,
  emptyLabel,
  errorLabel,
  minHeight,
  className,
  children,
}: QueryStateProps<T>): JSX.Element {
  // `minHeight` floors the h-full loading/error/empty views so they stay
  // visible inside bare, un-sized `<div>`s. Wrapping rather than threading
  // the style down keeps `ChartLoading` / `ChartEmpty` unchanged.
  const withMinHeight = (view: JSX.Element): JSX.Element =>
    minHeight === undefined ? view : <div style={{ minHeight }}>{view}</div>;

  if (query.isLoading) {
    return withMinHeight(<ChartLoading label={loadingLabel} className={className} />);
  }
  if (query.isError) {
    const message =
      typeof errorLabel === 'function' ? errorLabel(query.error) : errorLabel;
    return withMinHeight(<ChartEmpty message={message} className={className} />);
  }
  if (query.data === undefined || isEmpty(query.data)) {
    return withMinHeight(<ChartEmpty message={emptyLabel} className={className} />);
  }
  return <>{children(query.data)}</>;
}

export default QueryState;
